import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, QueryList, TemplateRef, ViewChildren } from "@angular/core";
import { TableCellAction, TableCellActionDataOut } from "@app/models";
import { TableService } from "@app/services";
import { AdvancedSortableDirective, SortEvent } from "@app/shared/directives/advanced-sortable.directive";
import { AbstractGridComponent } from "../abstract-grid.component";
import { sortTableByType, tableDateFormat } from "./../table.utils";

@Component({
  selector: "app-base-grid",
  templateUrl: "./base-grid.component.html",
  styleUrls: ["./base-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseGridComponent<T extends ({ id: number } | { id: string }) & Record<string, any>> extends AbstractGridComponent<T> {
  readonly dateFormat = tableDateFormat;

  @Input() wrappedByCard = false;
  @Input() hidePagination = false;
  /** When true, clicking anywhere on a row toggles its checkbox selection. */
  @Input() rowClickSelects = false;
  @Input() selectedRowDetailTemplate: TemplateRef<{ $implicit: T }> | null = null;
  @Input() hasRowDetail: (row: T) => boolean = () => true;
  @Input() set data(value: T[]) {
    if (value) {
      this.selectionMap.clear();
      this.tableDataSrc = this.normalizeTableData(this.columnData, value, this.datePipe, this.columnDateFormat);
      this.updateTableData();
      this.applyInitialSelection();
    }
  }

  /** Ids to pre-select once the data is loaded (checkbox columns). Empty keeps the default no-selection behavior. */
  @Input() set selectedRowIds(ids: (number | string)[]) {
    this.initialSelectedRowIds = ids ?? [];
    this.applyInitialSelection();
  }

  @Input() set searchString(search: string) {
    this.search = search;
    this.pageIndex = 1;
    this.updateTableData();
  }

  @Input() set defaultSort(sorts: SortEvent) {
    if (sorts) {
      this.sorts = sorts;
      this.tableDataSrc = this.normalizeTableData(this.columnData, this.tableDataSrc, this.datePipe, this.columnDateFormat);
      this.updateTableData();
    }
  }

  @Output() tableAction = new EventEmitter<TableCellActionDataOut>();

  @ViewChildren(AdvancedSortableDirective) headers: QueryList<AdvancedSortableDirective>;

  protected sorts: SortEvent;
  protected search = "";
  protected pageIndex = 1;
  protected pageSize = 10;
  protected total = this.tableData.length;
  protected isAllRowsSelected = false;
  protected selectionMap = new Map<number | string, boolean>();
  protected tableDataSrc: T[] = [];
  private initialSelectedRowIds: (number | string)[] = [];

  constructor(protected tableService: TableService, protected datePipe: DatePipe) {
    super(datePipe);
  }

  onSort(e: SortEvent): void {
    const column = e.column;
    const direction = e.direction === "desc" ? "desc" : "asc";

    this.sorts = { column, direction };
    this.headers = this.tableService.header(this.headers, column);
    this.updateTableData();
  }

  onPagination(pageSize: number = this.pageSize): void {
    this.pageSize = pageSize;
    this.updateTableData();
  }

  onTableAction(action: TableCellAction, itemId: number | string): void {
    this.tableAction.emit({ action, itemId });
  }

  /** Disable the header "select all" checkbox when no row can be selected (e.g. a read-only grid). */
  protected get isSelectAllDisabled(): boolean {
    const selectColumn = this.columnData?.find((c) => c.dataType === "rowSelect");

    if (!selectColumn?.isRowSelectable) {
      return false;
    }

    return !this.tableData.some((row) => selectColumn.isRowSelectable(row));
  }

  protected get visibleColumnCount(): number {
    return this.columnData.filter((c) => !c.hidden).length + (this.actionList?.length ? 1 : 0);
  }

  protected hasRowDetailRow(row: T): boolean {
    return !!this.selectedRowDetailTemplate && this.hasRowDetail(row);
  }

  onAllRowsSelect(event: Event): void {
    this.isAllRowsSelected = !!(event.target as HTMLInputElement).checked;

    if (this.isAllRowsSelected) {
      this.tableData.forEach((item) => {
        this.selectionMap.set(item.id, true);
      });
    } else {
      this.selectionMap.clear();
    }

    this.emitSelectedRowIds();
  }

  onRowSelect(item: T, event: Event): void {
    const isSelected = !!(event.target as HTMLInputElement).checked;

    this.toggleRowSelection(item, isSelected);
  }

  /** Toggle the whole row's selection (enabled per-table via `rowClickSelects`). */
  onRowClick(item: T, event: Event): void {
    if (!this.rowClickSelects) {
      return;
    }
    // Ignore clicks that originate from interactive cell content (the checkbox, action buttons, links).
    if ((event.target as HTMLElement).closest("input, button, a, label")) {
      return;
    }
    const selectColumn = this.columnData.find((c) => c.dataType === "rowSelect");
    if (selectColumn?.isRowSelectable && !selectColumn.isRowSelectable(item)) {
      return;
    }

    this.toggleRowSelection(item, !this.selectionMap.has(item.id));
  }

  private toggleRowSelection(item: T, isSelected: boolean): void {
    if (isSelected) {
      this.selectionMap.set(item.id, true);
    } else {
      this.selectionMap.delete(item.id);
    }
    this.isAllRowsSelected = this.selectionMap.size === this.tableData.length && this.tableData.length > 0;

    this.emitSelectedRowIds();
  }

  protected emitSelectedRowIds(): void {
    const selectedIds: (string | number)[] = Array.from(this.selectionMap.keys());

    this.selectedRowsChange.emit(selectedIds);
  }

  private applyInitialSelection(): void {
    if (!this.initialSelectedRowIds.length || !this.tableDataSrc.length) {
      return;
    }
    this.initialSelectedRowIds.forEach((id) => {
      if (this.tableDataSrc.some((row) => row.id === id)) {
        this.selectionMap.set(id, true);
      }
    });
    this.isAllRowsSelected = this.selectionMap.size === this.tableDataSrc.length && this.tableDataSrc.length > 0;
  }

  protected filterTable(list: T[]): T[] {
    if (this.search) {
      return list.filter((item: T) =>
        this.columnData.some((column) => {
          if (item.hasOwnProperty(column.key) && !column.hidden) {
            return `${item[column.key]}`.toLowerCase().includes(this.search.toLowerCase());
          }

          return false;
        })
      );
    }

    return list;
  }

  protected updateTableData(): void {
    let table = this.tableDataSrc.slice();

    table = this.filterTable(table);

    if (this.sorts?.column) {
      const targetColumn = this.columnData.find((c) => c.key === this.sorts.column);

      if (targetColumn?.dataType) {
        table = sortTableByType(table, this.sorts, targetColumn?.dataType);
      }
    }

    this.total = table.length;
    this.tableData = this.tableService.pagination(table, this.pageIndex, this.pageSize);
  }
}
